import { useCallback, useMemo } from 'react';
import { EngineService } from '@/services/engine';
import { Event } from '@/types/event';
import { PaginatedResponse } from '@/types/responses';
import { useQueryFiltersStore } from '@/stores';
import { toUtcTimestamp } from '@/utils/date';
import { getDateRangeFromFilter } from '@/utils/query-filters';
import { EVENTS_PAGE_SIZE, PaginatedEventsResult } from './eventsDataShared';
import { usePaginatedList } from './usePaginatedList';
import type { VehicleOccurrence } from '@/types/vehicle';

export interface UseVehicleEventsDataOptions {
    vehicleId: string;
}

export interface VehicleEventsDataResult extends PaginatedEventsResult<Event> {
    occurrences: VehicleOccurrence[];
}

export function useVehicleEventsData({
    vehicleId,
}: UseVehicleEventsDataOptions): VehicleEventsDataResult {
    const dateTime = useQueryFiltersStore((state) => state.filters.dateTime);

    const dateRange = useMemo(
        () => getDateRangeFromFilter(dateTime),
        [dateTime],
    );
    const startTime = useMemo(
        () => (dateRange ? toUtcTimestamp(dateRange.startDate) : undefined),
        [dateRange],
    );
    const endTime = useMemo(
        () => (dateRange ? toUtcTimestamp(dateRange.endDate) : undefined),
        [dateRange],
    );

    const cacheKey = `vehicle-events-${vehicleId}-${startTime}-${endTime}`;

    const fetchPage = useCallback(
        (pageIndex: number): Promise<PaginatedResponse<VehicleOccurrence>> =>
            EngineService.getVehicleOccurrences(vehicleId, {
                page: pageIndex + 1,
                size: EVENTS_PAGE_SIZE,
                ...(startTime != null && endTime != null
                    ? { startTime, endTime }
                    : {}),
            }),
        [vehicleId, startTime, endTime],
    );

    const result = usePaginatedList<VehicleOccurrence>({
        cacheKey,
        pageSize: EVENTS_PAGE_SIZE,
        fetchPage,
    });

    const occurrences = result.events;
    const events = useMemo(
        () => occurrences.map((occurrence) => occurrence.event),
        [occurrences],
    );

    return { ...result, events, occurrences };
}
